import React from 'react';
import { Route } from 'react-router-dom';
import { RouteProps, ExtractRouteParams } from 'react-router';
import { AllApplicationPermissions } from '../../Config/PermissionConfig';
import { isUserAuthorised } from '../../Hooks/usePermissionLoader';
import { ErrorAccessDenied } from '../SplashScreens/ErrorAccessDenied';

interface AuthorizeRouteProps<Path extends string = string>
    extends Omit<RouteProps<Path, ExtractRouteParams<Path, string>>, 'children'> {
    permission: AllApplicationPermissions | AllApplicationPermissions[] | boolean;
    customErrorRender?(): React.ReactNode;
}

export const AuthorizedRoute = <Path extends string = string>({
    path,
    permission,
    component: Component,
    render,
    customErrorRender,
    ...rest
}: AuthorizeRouteProps<Path>): JSX.Element => (
    <Route
        {...rest}
        path={path}
        render={props => {
            const renderSuccess = () => {
                if (render) return render(props);
                if (Component) return <Component {...props} />;

                throw new Error(`AuthorizedRoute does not have a component or render passed to it - path: ${path}`);
            };

            const renderDenied = () => {
                if (customErrorRender) return customErrorRender();
                else return <ErrorAccessDenied />;
            };

            if (typeof permission === 'boolean') {
                if (permission) {
                    return renderSuccess();
                } else {
                    return renderDenied();
                }
            } else {
                const isAuthorised = isUserAuthorised(permission);
                if (!isAuthorised) {
                    return renderDenied();
                }

                return renderSuccess();
            }
        }}
    />
);
